// Time ruler above the waveform — a port of Photon's
// cpopsa/src/gui/time_ruler_component.cpp: major ticks with "2s" labels and
// minor ticks between them, intervals chosen from the pixel density. The
// widget shows the whole clip, so viewStart is always 0.

export interface TickIntervals {
  major: number;   // seconds
  minor: number;
}

export interface Tick {
  t: number;       // seconds
  x: number;       // CSS px from the ruler's left edge
  major: boolean;
  label: string;   // '' for minors and for t = 0
}

// The app reserves 30px for the ruler (navigation_constants.hpp
// kTimeRulerHeight): a 12px label line at the top, then the ticks growing up
// from the baseline — 14px majors, 6px minors — leaving 4px between label
// boxes and tick tops. The baseline itself is the waveform box's top border,
// drawn by the frame, not here.
export const RULER_HEIGHT = 30;
const TICK_COLOR = '#7E878F';   // theme::v2 mutedIcon
const LABEL_PX = 10;
const LABEL_BOX_H = 12;

// calculateTickIntervals: aim for one major tick per ~100 px.
export function tickIntervals(durationS: number, widthPx: number): TickIntervals {
  if (durationS <= 0 || widthPx <= 0) return { major: 0, minor: 0 };
  const target = (durationS * 100) / widthPx;
  const table: Array<[number, number, number]> = [
    [0.01, 0.01, 0.002], [0.02, 0.02, 0.005], [0.05, 0.05, 0.01], [0.1, 0.1, 0.02],
    [0.2, 0.2, 0.05], [0.5, 0.5, 0.1], [1, 1, 0.2], [2, 2, 0.5], [5, 5, 1],
    [10, 10, 2], [20, 20, 5], [30, 30, 10], [60, 60, 15], [120, 120, 30],
    [300, 300, 60], [600, 600, 120],
  ];
  for (const [limit, major, minor] of table) {
    if (target <= limit) return { major, minor };
  }
  return { major: 1200, minor: 300 };
}

// formatTimeLabel: "0.5s" / "2s" below a minute, "1:05" above.
export function formatTickLabel(t: number, major: number): string {
  if (Math.abs(t) < 0.001) return '';
  if (t >= 60) {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }
  if (major < 0.1) return `${t.toFixed(2)}s`;
  if (major < 1) return `${t.toFixed(1)}s`;
  return `${Math.round(t)}s`;
}

export function ticks(durationS: number, widthPx: number): Tick[] {
  const { major, minor } = tickIntervals(durationS, widthPx);
  if (major <= 0) return [];
  const out: Tick[] = [];
  const toX = (t: number) => (t / durationS) * widthPx;
  const count = Math.floor(durationS / minor + 1e-6);
  for (let i = 0; i <= count; i++) {
    const t = i * minor;
    const isMajor = Math.abs(t / major - Math.round(t / major)) < 1e-6;
    out.push({ t, x: toX(t), major: isMajor, label: isMajor ? formatTickLabel(t, major) : '' });
  }
  return out;
}

// Owns the ruler canvas: DPR-aware sizing on resize, redraw only on resize or
// duration change (the ruler is static while the clip plays).
export class RulerCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private fontFamily: string;
  private widthCss = 0;
  private heightCss = 0;
  private dpr = 1;
  private durationS = 0;
  private resizeObserver: ResizeObserver;

  constructor(canvas: HTMLCanvasElement, fontFamily: string) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to acquire 2D canvas context');
    this.ctx = ctx;
    this.fontFamily = fontFamily;
    this.resizeObserver = new ResizeObserver(() => this.handleResize());
    this.resizeObserver.observe(canvas);
    this.handleResize();
  }

  setDuration(seconds: number): void {
    if (seconds === this.durationS) return;
    this.durationS = seconds;
    this.draw();
  }

  dispose(): void {
    this.resizeObserver.disconnect();
  }

  private handleResize(): void {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (w === this.widthCss && h === this.heightCss && dpr === this.dpr) return;
    this.widthCss = w;
    this.heightCss = h;
    this.dpr = dpr;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.draw();
  }

  private draw(): void {
    drawRuler(this.ctx, this.widthCss, this.heightCss, this.durationS, this.fontFamily);
  }
}

// Draws into a ctx already scaled for DPR. `font` is the CSS font family stack.
export function drawRuler(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  durationS: number,
  fontFamily: string,
): void {
  ctx.clearRect(0, 0, w, h);
  if (durationS <= 0 || w <= 0) return;
  ctx.fillStyle = TICK_COLOR;
  ctx.font = `${LABEL_PX}px ${fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (const tick of ticks(durationS, w)) {
    const x = Math.round(tick.x);
    if (tick.major) {
      // 14 px tall, 1.5 wide; label centred on the tick in the 12px top line
      ctx.fillRect(x - 0.75, h - 14, 1.5, 14);
      if (tick.label) ctx.fillText(tick.label, x, LABEL_BOX_H / 2);
    } else {
      ctx.fillRect(x - 0.5, h - 6, 1, 6);
    }
  }
}
